import React, { useState, useEffect, useCallback } from "react";
import api_services from "../../../src/services/api_services";
import toast from "react-hot-toast";

const CouponModal = ({
  onClose,
  totalAmount,
  setTotalAmount,
  setAppliedCoupon,
  products,
  isLoggedIn = true,
  applyCoupon: parentApplyCoupon,
  onOpen,
  onClose: parentOnClose,
}) => {
  // Emit event when modal is opened/closed to control bottom nav visibility
  useEffect(() => {
    const event = new CustomEvent('couponModalState', { 
      detail: { isOpen: true } 
    });
    window.dispatchEvent(event);
    
    // Cleanup
    return () => {
      const cleanupEvent = new CustomEvent('couponModalState', { 
        detail: { isOpen: false } 
      });
      window.dispatchEvent(cleanupEvent);
    };
  }, []);

  const [coupons, setCoupons] = useState([]);
  const [allCoupons, setAllCoupons] = useState([]); // Store full list (including inactive)
  const [couponCode, setCouponCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchCartData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Pass isLoggedIn to the API call to handle guest vs. logged-in users
      const response = await api_services.fetchAllCoupons({ isLoggedIn });
      if (response.success && response.data) {
        const formattedCoupons = response.data.map((coupon) => ({
          id: coupon.id,
          code: coupon.coupon_code,
          value: coupon.coupon_value,
          isPercentage: coupon.isPercentage === "yes",
          ca_id: coupon.ca_id,
          max_discount: coupon.max_discount, // Optional cap for percentage/overall discount
          status: coupon.status, // Preserve raw status from API (expects 1 for active)
          requiresLogin: coupon.requiresLogin || false, // New field to indicate if coupon requires login
        }));
        // Keep the full list for manual entry checks
        setAllCoupons(formattedCoupons);
        // Only include active coupons (status === 1)
        const activeCoupons = formattedCoupons.filter(
          (c) => c.status === 1 || c.status === "1"
        );
        // Further filter based on isLoggedIn if necessary
        const availableCoupons = isLoggedIn
          ? activeCoupons
          : activeCoupons.filter((coupon) => !coupon.requiresLogin);
        setCoupons(availableCoupons);
        if (availableCoupons.length === 0 && !isLoggedIn) {
          toast.error("No coupons available for guest users. Please log in to view more.");
        }
      } else {
        console.error("Failed to fetch coupons:", response.message);
        toast.error(response.message || "Failed to fetch coupons.");
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
      toast.error("Error fetching coupons.");
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchCartData();
  }, [fetchCartData, isLoggedIn]); // Include fetchCartData and isLoggedIn as dependencies

  const calculateCategoryTotal = (ca_id, checkForSaleItems = false) => {
    if (!products || !Array.isArray(products)) {
      return 0;
    }

    // Filter products based on category ID if provided
    let filteredProducts = products;
    if (ca_id !== null && ca_id !== "" && ca_id !== undefined) {
      filteredProducts = products.filter(
        (product) => String(product.ca_id) === String(ca_id)
      );
    }

    // Calculate total for eligible products (not on sale when checkForSaleItems is true)
    const total = filteredProducts.reduce((sum, item) => {
      // Skip sale items if we're checking for them
      if (checkForSaleItems && (item.on_sale === 1 || item.on_sale === '1')) {
        return sum;
      }
      
      const isContactLens = String(item.ca_id) === "3";
      const itemPrice = isContactLens
        ? parseFloat(item.price_without_gst || item.contact_lens_price || item.price || 0)
        : parseFloat(item.price_with_gst || item.price || 0);
      const quantity = parseInt(item.quantity || item.cart_quantity || 1, 10);
      return sum + itemPrice * quantity;
    }, 0);
    
    return total;
  };

  const applyCoupon = (coupon) => {
    // Check if coupon requires login and user is not logged in
    if (coupon.requiresLogin && !isLoggedIn) {
      toast.error("Please log in to apply this coupon.");
      return;
    }

    // Get all products in the category (if category is specified)
    const categoryProducts = coupon.ca_id === null || coupon.ca_id === "" || coupon.ca_id === undefined
      ? products
      : products.filter(p => String(p.ca_id) === String(coupon.ca_id));

    if (categoryProducts.length === 0) {
      toast.error(`No products found for this coupon category (ca_id: ${coupon.ca_id || 'all'})`);
      return;
    }

    // Filter out sale items
    const nonSaleProducts = categoryProducts.filter(p => p.on_sale !== 1 && p.on_sale !== '1');
    
    if (nonSaleProducts.length === 0) {
      toast.error("Coupon not applicable on flash sale products");
      return;
    }

    // Calculate total for non-sale items only
    const eligibleTotal = nonSaleProducts.reduce((sum, item) => {
      const isContactLens = String(item.ca_id) === "3";
      const itemPrice = isContactLens
        ? parseFloat(item.price_without_gst || item.contact_lens_price || item.price || 0)
        : parseFloat(item.price_with_gst || item.price || 0);
      const quantity = parseInt(item.quantity || item.cart_quantity || 1, 10);
      return sum + (itemPrice * quantity);
    }, 0);

    // Calculate discount based on non-sale items only
    let discount = coupon.isPercentage
      ? (eligibleTotal * parseFloat(coupon.value)) / 100
      : Math.min(parseFloat(coupon.value), eligibleTotal);

    // Apply max discount cap if provided (supports number or numeric string)
    const maxCap = coupon.max_discount !== null && coupon.max_discount !== undefined
      ? parseFloat(coupon.max_discount)
      : null;
    if (!isNaN(maxCap) && maxCap > 0) {
      discount = Math.min(discount, maxCap);
    }

    // Prepare the coupon data with correct values
    // Pass through original percentage/value and max_discount so parent can
    // re-compute on future cart changes while respecting the cap.
    const couponData = {
      ...coupon,
      discount, // immediate discount for current cart state
      categoryTotal: eligibleTotal,
      applicableProducts: nonSaleProducts.length,
      applicableProductIds: nonSaleProducts.map(p => p.id || p.product_id)
    };

    // Use parent applyCoupon function if available
    if (parentApplyCoupon) {
      // Exclude precomputed discount so parent always recomputes based on latest cart
      const { discount: _ignoredDiscount, ...parentPayload } = couponData;
      parentApplyCoupon(parentPayload);
      onClose();
      return;
    }

    // Otherwise, update local state
    setAppliedCoupon(couponData);
    toast.success(`Coupon applied to ${nonSaleProducts.length} item(s)!`);
    onClose();
  };

  const handleCouponInput = () => {
    // Search against full coupon list so manual entry can apply even inactive coupons
    const matchedCoupon = allCoupons.find(
      (c) => c.code.toUpperCase() === couponCode.trim().toUpperCase()
    );
    if (!matchedCoupon) {
      toast.error("Invalid coupon code");
      return;
    }
    applyCoupon(matchedCoupon);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-white h-full shadow-lg transform transition-transform duration-300"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slide-in-right 0.3s forwards" }}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <button
            className="text-gray-600 hover:text-gray-800"
            onClick={onClose}
            aria-label="Close modal"
          >
            ←
          </button>
          <h2 className="text-lg font-semibold">Coupons for you</h2>
          <button
            className="text-gray-600"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-4">
          {isLoading ? (
            <p className="text-center text-gray-500">Loading coupons...</p>
          ) : (
            <>
              <div className="relative w-full">
                <textarea
                  placeholder="ENTER COUPON CODE"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="w-full border border-gray-300 rounded-md py-2 pl-3 pr-24 outline-none h-12 resize-none"
                ></textarea>
                <button
                  onClick={handleCouponInput}
                  className="absolute top-0 right-2 bg-[#1F509A] text-white px-4 h-12 flex items-center justify-center rounded-md hover:bg-[#1F509A]/90 transition"
                  disabled={isLoading}
                >
                  Apply
                </button>
              </div>
              {coupons.length > 0 ? (
                coupons.map((coupon) => {
                  const categoryTotal = calculateCategoryTotal(coupon.ca_id);
                  const eligibleTotal = calculateCategoryTotal(coupon.ca_id, true);
                  const hasApplicableProducts = categoryTotal > 0;
                  const hasNonSaleProducts = eligibleTotal > 0;
                  const isRestricted = coupon.requiresLogin && !isLoggedIn;
                  return (
                    <div
                      key={coupon.id}
                      className={`border rounded-lg p-3 mb-3 flex justify-between items-center ${
                        !hasApplicableProducts || isRestricted || !hasNonSaleProducts
                          ? "opacity-50 bg-gray-100"
                          : ""
                      }`}
                    >
                      <div>
                        <p className="font-semibold">{coupon.code}</p>
                        <p className="text-gray-600">
                          {coupon.isPercentage
                            ? `${coupon.value}% off`
                            : `₹${coupon.value} off`}
                        </p>
                        {coupon.isPercentage && coupon.max_discount !== null && coupon.max_discount !== undefined && String(coupon.max_discount).trim() !== '' && (
                          <p className="text-xs text-gray-500">Max discount ₹{parseFloat(coupon.max_discount)}</p>
                        )}
                        {hasApplicableProducts && !isRestricted && (
                          <p className="text-xs text-green-600">
                            {hasNonSaleProducts 
                              ? `Applicable on ₹${eligibleTotal.toFixed(2)} worth items`
                              : 'No eligible products (sale items excluded)'
                            }
                          </p>
                        )}
                        {!hasApplicableProducts ? (
                          <p className="text-xs text-red-500">
                            No applicable products in cart
                          </p>
                        ) : !hasNonSaleProducts ? (
                          <p className="text-xs text-red-500">
                            Coupon not applicable on flash sale products
                          </p>
                        ) : null}
                        {isRestricted && (
                          <p className="text-xs text-red-500">
                            Login required to apply this coupon
                          </p>
                        )}
                      </div>
                      <button
                        className={`px-4 py-2 rounded transition ${
                          hasApplicableProducts && hasNonSaleProducts && !isRestricted
                            ? "bg-[#1F509A] text-white hover:bg-[#1F509A]/90"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                        onClick={() => applyCoupon(coupon)}
                        disabled={!hasApplicableProducts || !hasNonSaleProducts || isRestricted}
                      >
                        Apply
                      </button>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-yellow-500">
                  {isLoggedIn
                    ? "Sorry! No Coupons available at this moment."
                    : "No coupons available for guest users. Please log in to view more."}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponModal;